import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IChoice } from 'src/app/models/application-form/application-form.model';
import { FormGroupStringType } from 'src/app/models/shared/shared-types';
import { InputFocusAbstractService } from 'src/app/services/input-focus/input-focus.abstract.service';

@Component({
    selector: 'appform-dropdown-input',
    templateUrl: './dropdown-input.component.html',
    styleUrls: ['./dropdown-input.component.css'],
    standalone: false
})
export class DropdownInputComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() question_id: string;
  @Input() answer_id: string;
  @Input() code: string;
  @Input() fieldType: string;
  @Input() text: string;
  @Input() label: string;
  @Input() error_text: string;
  @Input() help_text: string;
  @Input() required: boolean;
  @Input() placeholder: string;
  @Input() choices: IChoice[];
  @Input() multiple: boolean;
  @Input() useAlias: boolean;
  @Input() choice_id: string;
  @Input() choice_ids: string[] = [null];
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onControlValueChanges = new EventEmitter<any>();
  @ViewChild('input') input: ElementRef<any>;
  @Input() invalid: boolean;
  @Input() hide_label: boolean;
  @Input() readOnly: boolean;
  private _formcontrolUnsub = new Subject<void>();

  get displayHelpText(): boolean {
    return this.help_text ? true : false;
  }
  public paramFormGroup: FormGroup<FormGroupStringType>;
  public inputControl: FormControl<string | string[]>;

  get isInvalid(): boolean {
    this.invalid = this.inputControl.invalid && this.inputControl.touched;
    return this.invalid;
  }

  get isMultiple(): boolean {
    return this.code === 'progressive_commercial_product' || this.code === 'progressive_commercial_coverage_types' ? false : this.multiple;
  }

  constructor(
    private inputFocusService: InputFocusAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.choice_id && this.choice_id && this.inputControl) {
      this.inputControl.setValue(this.choice_id);
    }

    if (
      changes?.choice_ids &&
      this.choice_ids?.length > 0 &&
      this.inputControl &&
      changes?.choice_ids?.currentValue[0] !== changes?.choice_ids?.previousValue[0]
    ) {
      this.inputControl.setValue(this.choice_ids[0]);
    }
  }

  ngAfterViewInit(): void {
    this.inputFocusService.emitAgentInputIsSet(this.input);
    // This forces validation for prefilled fields
    if (this.inputControl?.value) {
      this.inputControl.markAsDirty();
      this.inputControl.markAllAsTouched();
    }
  }

  ngOnInit(): void {
    this.initControlGroup();
  }

  private initControlGroup() {
    const formControlsGroup: FormGroupStringType = {};
    this.paramFormGroup = new FormGroup<FormGroupStringType>(formControlsGroup);
    this.inputControl = new FormControl<string>(null, this.required ? [Validators.required] : null);
    if (this.multiple) this.inputControl.setValue(this.choice_ids[0]);
    else this.inputControl.setValue(this.choice_id);
    this.paramFormGroup.addControl(this.question_id, this.inputControl);
    this.inputControl.valueChanges
      .pipe(
        debounceTime(300), //had to added debounceTime to avoid Redux Async Error from SDK. Will Re-evaluate after discussing with BP.
        takeUntil(this._formcontrolUnsub)
      )
      .subscribe(
        (value: any) => {
          const event = {
            type: 'onControlValueChanges',
            data: {
              question_id: this.question_id,
              answer_id: this.answer_id,
              code: this.code,
              field_type: this.fieldType,
              choice_id: this.multiple ? null : value,
              choice_ids: this.multiple ? [value] : null,
              invalid: this.isInvalid
            }
          };
          this.onControlValueChanges.emit(event);
        },
        (err: string) => {
          this.loggerService.postLogData(LogLevel.Error, 'dropdown-input: initControlGroup', err, '');
          this._formcontrolUnsub.next();
          this._formcontrolUnsub.complete();
        }
      );
  }

  public choiceText(choice: IChoice): string {
    if (this.useAlias) {
      return choice.alias;
    } else {
      return choice.value;
    }
  }

  public getAriaLabel() {
    let labelBuilder = this.label;
    if (this.isInvalid) {
      labelBuilder += this.error_text;
    }
    return labelBuilder;
  }

  public getCurrentChoice(): string {
    let currentChoice = null;
    this.choices.forEach((choice) => {
      if (choice.id == (this.choice_ids[0] ? this.choice_ids[0] : this.choice_id)) {
        currentChoice = choice;
      }
    });
    return currentChoice == null ? '' : currentChoice.value;
  }
}
